import { fromScaled, toScaled } from '@colplat/protocol';
import {
  byId, groupBy,
  type Ledger, type SplitRow, type WalletRow, type WalletTransferRow,
} from './rows.js';
import { walletColours } from './walletColours.js';
import { enteredBefore } from './accounts.js';
import type { WalletHistory } from './register.js';

/**
 * The envelope view over the double entry (ADR-0023), computed from rows so
 * that it computes in both places (ADR-0025).
 *
 * Nothing here is a second bookkeeping system. Every number is a sum over
 * ordinary split rows, which is what lets the page say the thing a budgeting
 * app cannot: the wallets add up to the money actually on hand, currency by
 * currency. It holds by construction, because a money leg carries at most one
 * wallet and anything not earmarked is counted as Unassigned.
 *
 * Per currency throughout, and never converted. A wallet is short in forints
 * or in euros, never just "short".
 */

export const UNASSIGNED = 'unassigned';

/**
 * What money nobody has earmarked is called, in full and in a notch.
 *
 * There is no Unassigned row in the database and there is deliberately not
 * going to be one: NULL already means exactly this, and a real row would need
 * creating, protecting from deletion and keeping out of the lender lists. So
 * its short name cannot come from `wallet.shortName` the way every other
 * wallet's does, and is a constant instead.
 *
 * The empty set, because that is what it is: a leg belonging to no wallet.
 * It is also the one mark here that cannot be read as somebody's
 * abbreviation — every other notch holds a name, and a reader scanning the
 * column needs to see at a glance that this one is not.
 */
export const UNASSIGNED_WALLET = 'Unassigned';
export const UNASSIGNED_SHORT = '\u2205';

/** Legs that carry money. A wallet on any other kind of leg would break the identity. */
const MONEY_TYPES: ReadonlySet<string> = new Set(['ASSET', 'LIABILITY']);

export interface WalletDto {
  id: string;
  name: string;
  /** `wallet.shortName`, empty when nobody has set one. Never `name` substituted in — the
      page that shows both wants to know which it has. */
  shortName: string;
  /**
   * The colour this wallet is drawn in: its own `color`, or the palette's
   * pick when it has none (`walletColours`). Always a `#rrggbb` — what the
   * picker should show is the colour in force, not an empty field.
   */
  color: string;
  parentId: string | null;
  sortOrder: number;
  own: Record<string, string>;
  rollup: Record<string, string>;
  shortIn: string[];
}

export interface BalanceRow {
  walletId: string | null;
  currencyCode: string;
  total: string;
}

/**
 * Exact money, always.
 *
 * Scaled integers rather than floats because every one of these is a
 * cumulative sum over hundreds of rows, which is exactly where a half-cent
 * becomes a visible discrepancy in a figure the page claims is an identity.
 */
function addTo(target: Map<string, bigint>, ccy: string, amount: string): void {
  target.set(ccy, (target.get(ccy) ?? 0n) + toScaled(amount));
}

function fixed(map: Map<string, bigint>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [ccy, v] of map) {
    if (v === 0n) continue;
    out[ccy] = fromScaled(v);
  }
  return out;
}

/**
 * The earmark moves debts imply, as legs: when borrowed money left the
 * BORROWER's own wallet.
 *
 * A debt never moves balances by itself (ADR-0023, amendment of
 * 2026-10-18) — right for "Maintenance paid this for the car", where the
 * money already left the lender's wallet and the car simply owes it. The
 * other shape was reported from use: 2026-02-12, Renovation pays 7,240,000
 * out of its own wallet, and 2,357,883 of it was Car's, lent. Without a move
 * Renovation reads 2.36M over and Car still holds 2.36M that was spent — the
 * book adds up, the earmarks do not. The four-leg version of that entry
 * (Car → Renovation, then the payment) moves it, and so does this.
 *
 * The rule (amendment of 2026-10-19): the debt's leg is the borrower's own
 * and goes OUT — or it is in no wallet, and the entry's money left the
 * borrower's wallet and not the lender's. Then the debt's amount moves from
 * lender to borrower, on the same account and in the same currency as the
 * money that left.
 *
 * In THAT money's currency, as that money's share (2026-10-20). A debt can
 * be owed in the currency that arrived while the money left in another:
 * the mattress, paid from the euro card, billed and owed in forints. The
 * move is then the same fraction of the euros that the debt is of the
 * forints — all of them for a debt on the whole bill. It used to need the
 * two currencies to match, and between a euro card and a forint bill moved
 * nothing at all: the borrower's euros took the payment, and the lender's
 * still read as unspent. Every other shape is unchanged: a lender paying, or money
 * ARRIVING in the borrower's wallet, which its legs already say.
 *
 * Grants too: a hand-over moves the money as surely as a loan does; it is
 * only the debt that it leaves out.
 *
 * The ids start with `!`, which sorts before any uuid, so within the entry
 * the borrowed money arrives before it is spent and the running total never
 * dips through a shortfall that did not happen.
 */
export function debtShiftLegs(ledger: Ledger): SplitRow[] {
  const legsByTx = groupBy(ledger.splits(), (s) => s.transactionId);
  const out: SplitRow[] = [];
  for (const wt of ledger.walletTransfers()) {
    if (!wt.amountSplitId) continue;
    const legs = legsByTx.get(wt.transactionId) ?? [];
    const leg = legs.find((l) => l.id === wt.amountSplitId);
    if (!leg) continue;
    let host: SplitRow | undefined;
    if (leg.walletId === wt.toWalletId && toScaled(leg.amount) < 0n) {
      host = leg;
    } else if (!leg.walletId) {
      const leaving = legs.filter((l) => l.walletId && toScaled(l.amount) < 0n);
      if (leaving.some((l) => l.walletId === wt.toWalletId) && !leaving.some((l) => l.walletId === wt.fromWalletId)) {
        const mine = leaving.filter((l) => l.walletId === wt.toWalletId);
        host = mine.find((l) => l.currencyCode === leg.currencyCode) ?? mine[0];
      }
    }
    if (!host) continue;
    const abs = (v: bigint): bigint => (v < 0n ? -v : v);
    const whole = abs(toScaled(leg.amount));
    const owed = wt.amount !== undefined && wt.amount !== null && wt.amount !== '' ? toScaled(wt.amount) : whole;
    if (whole === 0n) continue;
    // The host's share: the debt's fraction of its own leg, of the money that
    // left. The same figure when the debt is on the host, or in its currency
    // on a leg of the same size.
    const part = host === leg ? owed : (abs(toScaled(host.amount)) * owed) / whole;
    const shift = (sign: 1n | -1n, walletId: string, side: string): SplitRow => ({
      id: `!shift:${wt.id}:${side}`,
      transactionId: wt.transactionId,
      accountId: host.accountId,
      memo: '',
      value: fromScaled(sign * part),
      amount: fromScaled(sign * part),
      currencyCode: host.currencyCode,
      walletId,
      shiftOf: wt.id,
    });
    out.push(shift(-1n, wt.fromWalletId, 'lent'), shift(1n, wt.toWalletId, 'borrowed'));
  }
  return out;
}

/**
 * Every leg as the WALLETS see it: the entry's own, plus the moves debts
 * imply (`debtShiftLegs`). What wallet balances, wallet logs, wallet charts
 * and shortfalls read. Everything about accounts, reports, budgets, bank
 * matching and the editors reads `ledger.splits()` — the moves net to zero on
 * their account and are not lines anybody wrote.
 */
export function walletLegs(ledger: Ledger): SplitRow[] {
  return [...ledger.splits(), ...debtShiftLegs(ledger)];
}

/** The money legs of the ledger — what every figure on the wallets page is a sum of. */
export function moneyLegs(ledger: Ledger): SplitRow[] {
  const accounts = byId(ledger.accounts());
  const live = new Set(ledger.transactions().map((t) => t.id));
  return walletLegs(ledger).filter((s) => {
    const account = accounts.get(s.accountId);
    return account !== undefined && MONEY_TYPES.has(account.type) && live.has(s.transactionId);
  });
}

/**
 * What a wallet held in one currency just before an entry — the wallets
 * and everything nested in them that `walletIds` names, as the wallets see
 * money (`moneyLegs`: debts' earmark moves included).
 *
 * What the short form's heads-up asks, on the entry's own date. It asked the
 * wallet's balance TODAY, which for an entry dated months back answered a
 * different question: a conversion on 8 May that left Renovation holding
 * 21,000 EUR was flagged as putting it over, because by October Renovation
 * was short — while the log, which asks each row's own moment, rightly put
 * no mark on it. Same order as `accountBalanceBefore` (`enteredBefore`).
 */
export function walletBalanceBefore(
  ledger: Ledger, walletIds: ReadonlySet<string>, currencyCode: string,
  at: { txDate: string; transactionId?: string | undefined },
): string {
  const earlier = enteredBefore(ledger, at);
  let sum = 0n;
  for (const leg of moneyLegs(ledger)) {
    if (leg.walletId === null || !walletIds.has(leg.walletId) || leg.currencyCode !== currencyCode) continue;
    if (earlier(leg.transactionId)) sum += toScaled(leg.amount);
  }
  return fromScaled(sum);
}

/** Per wallet, per currency. A NULL wallet comes back as Unassigned. */
export function walletBalances(ledger: Ledger): BalanceRow[] {
  const bags = new Map<string, Map<string, bigint>>();
  for (const leg of moneyLegs(ledger)) {
    const key = leg.walletId ?? '';
    let bag = bags.get(key);
    if (!bag) { bag = new Map(); bags.set(key, bag); }
    addTo(bag, leg.currencyCode, leg.amount);
  }
  const out: BalanceRow[] = [];
  for (const [key, bag] of bags) {
    for (const [currencyCode, total] of bag) {
      out.push({ walletId: key === '' ? null : key, currencyCode, total: fromScaled(total) });
    }
  }
  return out;
}

/**
 * Every wallet inside this one, however deep.
 *
 * The `seen` set is the cycle guard this codebase carries on every parent
 * walk: a pre-existing cycle is not this caller's problem, and hanging over
 * it helps nobody.
 */
export function descendantIds(rows: readonly WalletRow[], id: string): string[] {
  const byParent = new Map<string, WalletRow[]>();
  for (const w of rows) {
    if (!w.parentId) continue;
    const list = byParent.get(w.parentId);
    if (list) list.push(w); else byParent.set(w.parentId, [w]);
  }
  const out: string[] = [];
  const seen = new Set<string>([id]);
  const walk = (parent: string): void => {
    for (const child of byParent.get(parent) ?? []) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(id);
  return out;
}

export function buildWalletDtos(
  rows: readonly WalletRow[], balances: readonly BalanceRow[],
): { wallets: WalletDto[]; unassigned: Record<string, string>; cash: Record<string, string> } {
  const own = new Map<string, Map<string, bigint>>();
  const unassigned = new Map<string, bigint>();
  const cash = new Map<string, bigint>();

  for (const b of balances) {
    addTo(cash, b.currencyCode, b.total);
    if (b.walletId === null) { addTo(unassigned, b.currencyCode, b.total); continue; }
    let bag = own.get(b.walletId);
    if (!bag) { bag = new Map(); own.set(b.walletId, bag); }
    addTo(bag, b.currencyCode, b.total);
  }

  const colours = walletColours(rows);
  const wallets = rows.map((w) => {
    const rollup = new Map<string, bigint>(own.get(w.id) ?? new Map<string, bigint>());
    for (const id of descendantIds(rows, w.id)) {
      for (const [ccy, v] of own.get(id) ?? []) rollup.set(ccy, (rollup.get(ccy) ?? 0n) + v);
    }
    return {
      id: w.id,
      name: w.name,
      shortName: (w.shortName ?? '').trim(),
      color: colours.get(w.id)!,
      parentId: w.parentId,
      sortOrder: w.sortOrder ?? 0,
      own: fixed(own.get(w.id) ?? new Map<string, bigint>()),
      rollup: fixed(rollup),
      shortIn: [...rollup].filter(([, v]) => v < 0n).map(([c]) => c).sort(),
    };
  });

  return { wallets, unassigned: fixed(unassigned), cash: fixed(cash) };
}

/** The wallets page's whole top half, from rows. */
export function walletsView(
  ledger: Ledger,
): { wallets: WalletDto[]; unassigned: Record<string, string>; cash: Record<string, string> } {
  const rows = [...ledger.wallets()].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
  return buildWalletDtos(rows, walletBalances(ledger));
}

// --------------------------------------------------------------------- debts

/**
 * One transfer, as a step towards the figure the page shows.
 *
 * The page showed a net and nothing else, so there was no way to ask what it
 * was made of — reported as exactly that. A net of two loans and a repayment
 * is a number no one can check, and a number no one can check is one people
 * stop believing before they stop acting on it.
 */
export interface DebtLine {
  transferId: string;
  transactionId: string;
  txDate: string;
  description: string;
  kind: string;
  /**
   * The direction this one went, which is not always the row's.
   *
   * A line reading `-300` under "car owes renov" is a loan that went the
   * other way, and saying so is the difference between arithmetic a reader
   * can follow and a minus sign they have to take on trust.
   */
  fromWalletId: string;
  toWalletId: string;
  /**
   * Signed in the direction of the debt this belongs to: positive adds to
   * what the debtor owes, negative pays it off, zero counts for nothing.
   *
   * Relative to the direction as DISPLAYED, which is not always the
   * direction the transfer was entered in: a pair whose net crosses zero
   * flips, and every line flips with it. A line saying `+200` under
   * "A owes B" means this one put 200 onto what A owes.
   */
  effect: string;
  /** Where the pair stood after this one, same direction, same sign convention. */
  running: string;
  /**
   * How much of it paid back what the receiving side was owed before, and how
   * much was lent on top (`transferRoles`). Both `'0.0000'` on a grant.
   */
  paidBack: string;
  lent: string;
}

export interface DebtDto {
  debtorId: string;
  creditorId: string;
  currencyCode: string;
  amount: string;
  /** Oldest first, so the running total reads downwards like a register. */
  lines: DebtLine[];
}

export interface TransferAmount {
  transferId: string;
  transactionId: string;
  txDate: string;
  description: string;
  kind: string;
  fromWalletId: string;
  toWalletId: string;
  currencyCode: string;
  amount: string;
}

/**
 * Which way round a pair is held, before anyone knows who ends up owing.
 *
 * Two wallets lending to each other are one debt, not two, and which of them
 * is the debtor is only known once every transfer has been counted. So
 * everything accumulates against a fixed ordering of the pair — the ids
 * sorted — and the sign is flipped at the end if the answer came out the
 * other way. Sorting the ids rather than taking the first one seen makes the
 * grouping independent of the order the transfers arrive in.
 */
const pairOf = (a: string, b: string): [string, string] => (a < b ? [a, b] : [b, a]);

interface Pair {
  debtorId: string;
  creditorId: string;
  currencyCode: string;
  total: bigint;
  lines: Array<{ line: Omit<DebtLine, 'effect' | 'running'>; effect: bigint }>;
}

/**
 * The one distinction a transfer's `kind` still makes: is it coming back.
 *
 * "Loan" and "repay" were two answers to a question the direction already
 * answers. Money moving from A to B leaves B owing A that much more — or A
 * owing B that much less, which is the same sentence — so `netDebts` always
 * counted the two identically, and the user had to pick the right word for
 * arithmetic that did not need it. A partial repayment that tipped into a
 * new loan had no right word at all.
 *
 * So `repay` reads as `loan`, and whether a given one paid back, lent, or
 * both is worked out from where the pair stood (`transferRoles`). Stored
 * `repay` rows are left as they are: they already count the same, and
 * rewriting them would add history for no change in meaning. `grant` stays
 * apart, because "never coming back" is a fact the legs cannot carry.
 */
export function normalTransferKind(kind: string): string {
  return kind === 'repay' ? 'loan' : kind;
}

/** What one transfer did to the debt between its two wallets. */
export interface TransferRole {
  /** Paid off what the SENDING wallet owed the receiving one. */
  paidBack: string;
  /** The rest, which the receiving wallet now owes the sending one. */
  lent: string;
  /** The two together — all of its leg, or the part the row names — and in what. */
  amount: string;
  currencyCode: string;
}

/**
 * Which part of each transfer paid a debt back and which part was a new loan.
 *
 * Walked oldest first per pair and currency, in the order `netDebts` draws its
 * lines: 20,000 owed by Maintenance to Car, then 32,407.42 from Maintenance to
 * Car, is 20,000 paid back and 12,407.42 lent. A grant is left out — it
 * changes nothing between them.
 */
export function transferRoles(transfers: readonly TransferAmount[]): Map<string, TransferRole> {
  const ordered = [...transfers].sort((a, b) =>
    a.txDate.localeCompare(b.txDate)
    || a.transactionId.localeCompare(b.transactionId)
    || a.transferId.localeCompare(b.transferId));
  /** Per pair and currency: what the SECOND wallet of the sorted pair owes the first. */
  const owed = new Map<string, bigint>();
  const out = new Map<string, TransferRole>();
  for (const t of ordered) {
    if (normalTransferKind(t.kind) !== 'loan') continue;
    const [first, second] = pairOf(t.fromWalletId, t.toWalletId);
    const key = `${first}|${second}|${t.currencyCode}`;
    const before = owed.get(key) ?? 0n;
    // What the receiving wallet owed the sender before this.
    const toOwedFrom = t.toWalletId === second ? before : -before;
    const amount = toScaled(t.amount);
    const magnitude = amount < 0n ? -amount : amount;
    // Money that went the other way round (a negative leg) is read from the
    // side it actually left.
    const senderOwed = amount < 0n ? toOwedFrom : -toOwedFrom;
    const paidBack = senderOwed <= 0n ? 0n : senderOwed < magnitude ? senderOwed : magnitude;
    out.set(t.transferId, {
      paidBack: fromScaled(paidBack), lent: fromScaled(magnitude - paidBack),
      amount: fromScaled(magnitude), currencyCode: t.currencyCode,
    });
    owed.set(key, before + (t.toWalletId === second ? amount : -amount));
  }
  return out;
}

/**
 * Where two wallets stood with each other just before an entry, per currency.
 *
 * Positive is what `toWalletId` owes `fromWalletId`, in the direction of a
 * debt about to be recorded from one to the other — so the entry's own
 * amount simply adds to it. Negative is the other way round, and its size is
 * the crossing point: paying back up to there settles the debt, anything
 * beyond it is new lending.
 *
 * "Before" in the order `transferRoles` walks: by date, then transaction id.
 * The entry's own debts are left out, whatever their position, because the
 * question is what this entry finds when it arrives. A new entry has no id
 * yet and is taken as the last of its day. Grants count for nothing, as they
 * do everywhere. Currencies that net to zero are left out.
 */
export function standingBefore(
  transfers: readonly TransferAmount[],
  fromWalletId: string, toWalletId: string,
  at: { txDate: string; transactionId?: string | undefined },
): Record<string, string> {
  const sums = new Map<string, bigint>();
  for (const t of transfers) {
    if (normalTransferKind(t.kind) !== 'loan') continue;
    if (at.transactionId !== undefined && t.transactionId === at.transactionId) continue;
    const pair = (t.fromWalletId === fromWalletId && t.toWalletId === toWalletId)
      || (t.fromWalletId === toWalletId && t.toWalletId === fromWalletId);
    if (!pair) continue;
    const earlier = t.txDate < at.txDate
      || (t.txDate === at.txDate && (at.transactionId === undefined || t.transactionId < at.transactionId));
    if (!earlier) continue;
    const signed = t.toWalletId === toWalletId ? toScaled(t.amount) : -toScaled(t.amount);
    sums.set(t.currencyCode, (sums.get(t.currencyCode) ?? 0n) + signed);
  }
  const out: Record<string, string> = {};
  for (const [code, v] of sums) if (v !== 0n) out[code] = fromScaled(v);
  return out;
}

/**
 * Net position per pair, per currency, with the transfers that got it there.
 *
 * A grant is listed and counts for nothing: it was given, not lent. Leaving
 * it out entirely would answer "what makes up this debt?" and leave "why did
 * handing them 500 euros not change this?" unanswered, which is the same
 * question asked by somebody more worried.
 *
 * A repayment counts against the debt in the opposite direction, and a pair
 * whose net crosses zero flips rather than showing two rows that cancel.
 */
export function netDebts(transfers: readonly TransferAmount[]): DebtDto[] {
  const pairs = new Map<string, Pair>();
  const roles = transferRoles(transfers);

  for (const t of transfers) {
    const [first, second] = pairOf(t.fromWalletId, t.toWalletId);
    const key = `${first}|${second}|${t.currencyCode}`;
    let pair = pairs.get(key);
    if (!pair) {
      pair = {
        debtorId: first, creditorId: second, currencyCode: t.currencyCode, total: 0n, lines: [],
      };
      pairs.set(key, pair);
    }

    // Money from A to B puts that much onto what B owes A — whether it was
    // entered as a loan or as a repayment, which always counted the same
    // (`normalTransferKind`). A grant puts nothing.
    const kind = normalTransferKind(t.kind);
    const onSecond = t.toWalletId === second ? toScaled(t.amount) : -toScaled(t.amount);
    const effect = kind === 'grant' ? 0n : -onSecond;
    const role = roles.get(t.transferId);

    pair.total += effect;
    pair.lines.push({
      effect,
      line: {
        transferId: t.transferId,
        transactionId: t.transactionId,
        txDate: t.txDate,
        description: t.description,
        kind,
        fromWalletId: t.fromWalletId,
        toWalletId: t.toWalletId,
        paidBack: role?.paidBack ?? '0.0000',
        lent: role?.lent ?? '0.0000',
      },
    });
  }

  const out: DebtDto[] = [];
  for (const pair of pairs.values()) {
    if (pair.total === 0n) continue;
    // Held one way round while counting; shown the way round it came out.
    const flip = pair.total < 0n;
    const ordered = [...pair.lines].sort((a, b) =>
      a.line.txDate.localeCompare(b.line.txDate)
      || a.line.transactionId.localeCompare(b.line.transactionId)
      || a.line.transferId.localeCompare(b.line.transferId));

    let running = 0n;
    const lines = ordered.map(({ line, effect }) => {
      const shown = flip ? -effect : effect;
      running += shown;
      return { ...line, effect: fromScaled(shown), running: fromScaled(running) };
    });

    out.push({
      debtorId: flip ? pair.creditorId : pair.debtorId,
      creditorId: flip ? pair.debtorId : pair.creditorId,
      currencyCode: pair.currencyCode,
      amount: fromScaled(flip ? -pair.total : pair.total),
      lines,
    });
  }
  return out.sort((x, y) => Number(y.amount) - Number(x.amount));
}

/**
 * What each transfer is for, read off the leg it names.
 *
 * The whole leg is not stored on `walletTransfer` (ADR-0023): a second copy
 * would be free to drift from the legs, which the balance invariant already
 * governs. So it is read from the leg `amountSplitId` names — or, on rows
 * written before that, the leg whose `walletId` is the transfer's destination
 * and whose amount is positive. A PART of a leg is stored, because the legs
 * cannot say it (the 2026-10-18 amendment), and the server keeps it inside
 * its leg.
 */
export function transferAmounts(ledger: Ledger): TransferAmount[] {
  const legsByTx = groupBy(ledger.splits(), (s) => s.transactionId);
  // Its date and what it was called, so a reader can recognise the entry
  // rather than be handed an id.
  const txById = new Map(ledger.transactions().map((t) => [t.id, t]));
  const out: TransferAmount[] = [];
  for (const wt of ledger.walletTransfers()) {
    const tx = txById.get(wt.transactionId);
    const leg = transferLeg(legsByTx.get(wt.transactionId) ?? [], wt);
    if (!leg) continue;
    /*
     * All of the leg, or the part the row says (ADR-0023, amendment of
     * 2026-10-18). Unsigned either way: which way the debt runs is the
     * transfer's from and to, never the leg's sign. A debt recorded on the
     * payment leg — "Maintenance paid this for the car" — names a leg going
     * OUT, and reading its minus sign as the debt's would turn "the car owes"
     * into "the car is owed".
     */
    const whole = toScaled(leg.amount) < 0n ? -toScaled(leg.amount) : toScaled(leg.amount);
    const part = wt.amount !== undefined && wt.amount !== null && wt.amount !== '' ? toScaled(wt.amount) : undefined;
    out.push({
      transferId: wt.id,
      transactionId: wt.transactionId,
      txDate: tx?.txDate ?? '',
      description: tx?.description ?? '',
      kind: wt.kind,
      fromWalletId: wt.fromWalletId,
      toWalletId: wt.toWalletId,
      currencyCode: leg.currencyCode,
      amount: fromScaled(part ?? whole),
    });
  }
  return out;
}

/**
 * The one leg a transfer moved.
 *
 * `amountSplitId` says which, and when it is set there is nothing to work
 * out. It is nullable because rows written before it have none, and for
 * those the old rule still applies — the first positive leg in the
 * destination wallet.
 *
 * FIRST, not every one. The old code pushed a transfer per matching leg, so
 * a four-leg entry with one declared loan came out as two loans with the
 * same id, and the debt was the sum of both. One declaration is one
 * transfer whatever the legs look like; which leg it was is the question
 * `amountSplitId` answers, and a guess that returns several is not an answer
 * to it.
 */
function transferLeg<T extends { id: string; walletId: string | null; amount: string }>(
  legs: readonly T[], wt: WalletTransferRow,
): T | undefined {
  if (wt.amountSplitId) {
    const named = legs.find((l) => l.id === wt.amountSplitId);
    // A named leg that is no longer there is a dangling reference, not a
    // licence to guess: an edit removed it, and inventing a different leg's
    // amount would put a figure on this debt that nobody entered.
    return named;
  }
  return legs.find((l) => l.walletId === wt.toWalletId && toScaled(l.amount) > 0n);
}

export function debtsView(ledger: Ledger): DebtDto[] {
  return netDebts(transferAmounts(ledger));
}

/**
 * What a wallet — or a set of them, a wallet and everything inside it —
 * would hold if every debt between wallets were settled: plus what others
 * owe it, minus what it owes them, per currency.
 *
 * A debt between two wallets both inside the set cancels out within it and
 * is left alone: a parent lending to its own sub-wallet does not make the
 * parent's total any different. The book as a whole is unchanged by
 * construction — every debt is one wallet's minus and another's plus.
 *
 * `day`, when given, takes each debt as it stood at the end of that day —
 * its last line on or before it — for a page inspecting one date.
 */
export function settledBalances(
  bag: Readonly<Record<string, string>>, walletIds: ReadonlySet<string>,
  debts: readonly Pick<DebtDto, 'debtorId' | 'creditorId' | 'currencyCode' | 'amount' | 'lines'>[],
  day?: string,
): Record<string, string> {
  const sums = new Map<string, bigint>(Object.entries(bag).map(([c, v]) => [c, toScaled(v)]));
  for (const d of debts) {
    const owes = walletIds.has(d.debtorId);
    const owed = walletIds.has(d.creditorId);
    if (owes === owed) continue;
    let amount = d.amount;
    if (day !== undefined) {
      const upTo = d.lines.filter((l) => l.txDate <= day);
      if (upTo.length === 0) continue;
      amount = upTo[upTo.length - 1]!.running;
    }
    const v = toScaled(amount);
    sums.set(d.currencyCode, (sums.get(d.currencyCode) ?? 0n) + (owed ? v : -v));
  }
  const out: Record<string, string> = {};
  for (const [c, v] of sums) out[c] = fromScaled(v);
  return out;
}

/**
 * One debt line, as what it does to a set of wallets' settled balance: the
 * line's effect, signed plus where the set is owed and minus where it owes.
 * Summed up to a day they are `settledBalances` on that day; laid along a
 * register or a chart they are the same correction, entry by entry.
 *
 * Oldest first. A debt between two wallets both inside the set is left out,
 * for the reason `settledBalances` gives.
 */
export interface SettledShift {
  transactionId: string;
  txDate: string;
  currencyCode: string;
  delta: string;
}

export function settledShifts(
  walletIds: ReadonlySet<string>,
  debts: readonly Pick<DebtDto, 'debtorId' | 'creditorId' | 'currencyCode' | 'lines'>[],
): SettledShift[] {
  const out: SettledShift[] = [];
  for (const d of debts) {
    const owes = walletIds.has(d.debtorId);
    const owed = walletIds.has(d.creditorId);
    if (owes === owed) continue;
    for (const line of d.lines) {
      const v = toScaled(line.effect);
      if (v === 0n) continue;
      out.push({
        transactionId: line.transactionId, txDate: line.txDate, currencyCode: d.currencyCode,
        delta: fromScaled(owed ? v : -v),
      });
    }
  }
  // Stable, so lines of one day keep the order their debts listed them in.
  return out.sort((a, b) => a.txDate.localeCompare(b.txDate));
}

/**
 * A wallet's chart with its debts settled: on every day, its balance then
 * plus the shifts up to the end of that day.
 *
 * A day only a debt moved on gets a point of its own, and a currency only a
 * debt holds gets a series, so the line steps where the settled figure does.
 */
export function settledHistory(
  history: readonly WalletHistory[], shifts: readonly SettledShift[],
): WalletHistory[] {
  if (shifts.length === 0) return [...history];
  const codes = new Set([...history.map((h) => h.currencyCode), ...shifts.map((s) => s.currencyCode)]);
  return [...codes].sort().map((currencyCode) => {
    const own = history.find((h) => h.currencyCode === currencyCode)?.points ?? [];
    const mine = shifts.filter((s) => s.currencyCode === currencyCode);
    const days = [...new Set([...own.map((p) => p.date), ...mine.map((s) => s.txDate)])].sort();
    let i = 0;
    let j = 0;
    let actual = 0n;
    let shift = 0n;
    const points = days.map((date) => {
      for (; i < own.length && own[i]!.date <= date; i += 1) actual = toScaled(own[i]!.value);
      for (; j < mine.length && mine[j]!.txDate <= date; j += 1) shift += toScaled(mine[j]!.delta);
      return { date, value: fromScaled(actual + shift) };
    });
    return { currencyCode, points };
  });
}

/** The shifts up to the end of `day` (all of them without one), per currency. */
export function shiftsAsOf(shifts: readonly SettledShift[], day?: string): Record<string, string> {
  const sums = new Map<string, bigint>();
  for (const s of shifts) {
    if (day !== undefined && s.txDate > day) continue;
    sums.set(s.currencyCode, (sums.get(s.currencyCode) ?? 0n) + toScaled(s.delta));
  }
  const out: Record<string, string> = {};
  for (const [c, v] of sums) out[c] = fromScaled(v);
  return out;
}
